import { 
  ImportHistory, 
  ImportHistoryOptions, 
  ImportStatus, 
  ImportType, 
  ImportSummary,
  ImportedTransaction,
  UnknownVehicleRecord
} from '../types/importHistory';
import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  updateDoc,
  deleteDoc,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot
} from 'firebase/firestore';

/**
 * Service générique pour la gestion de l'historique des importations
 */
export class ImportHistoryService {
  private readonly collectionName = 'importHistory';
  private readonly transactionsCollectionName = 'importedTransactions';
  private readonly unknownVehiclesCollectionName = 'unknownVehicles';

  /**
   * Crée un nouvel enregistrement d'historique d'importation
   * @param importData Données de l'importation
   * @returns ID de l'enregistrement créé
   */
  async createImportHistory(importData: Omit<ImportHistory, 'id'>): Promise<string> {
    try {
      const importHistoryRef = collection(db, this.collectionName);
      
      // Convertir les dates en Timestamp pour Firestore
      const firestoreData = {
        ...importData,
        importDate: Timestamp.fromDate(importData.importDate),
        createdAt: Timestamp.now()
      };
      
      const docRef = await addDoc(importHistoryRef, firestoreData);
      return docRef.id;
    } catch (error) {
      console.error('Erreur lors de la création de l\'historique d\'importation:', error);
      throw new Error('Impossible de créer l\'historique d\'importation');
    }
  }

  /**
   * Récupère un enregistrement d'historique d'importation par son ID
   * @param importId ID de l'importation
   * @returns Données de l'importation
   */
  async getImportHistory(importId: string): Promise<ImportHistory | null> {
    try {
      const importHistoryRef = doc(db, this.collectionName, importId);
      const importHistorySnap = await getDoc(importHistoryRef);
      
      if (!importHistorySnap.exists()) {
        return null;
      }
      
      const data = importHistorySnap.data();
      
      // Convertir les Timestamp en Date
      return {
        id: importHistorySnap.id,
        ...data,
        importDate: (data.importDate as Timestamp).toDate()
      } as ImportHistory;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique d\'importation:', error);
      throw new Error('Impossible de récupérer l\'historique d\'importation');
    }
  }

  /**
   * Récupère la liste des importations selon les options spécifiées
   * @param organizationId ID de l'organisation
   * @param importType Type d'importation
   * @param options Options de filtrage et pagination
   * @returns Liste des importations et nombre total
   */
  async getImportHistoryList(
    organizationId: string,
    importType: ImportType,
    options: ImportHistoryOptions = {}
  ): Promise<{
    imports: ImportHistory[];
    total: number;
  }> {
    try {
      const importHistoryRef = collection(db, this.collectionName);
      
      // Construire la requête de base
      let q = query(
        importHistoryRef,
        where('organizationId', '==', organizationId),
        where('importType', '==', importType),
        orderBy('importDate', 'desc')
      );
      
      // Ajouter les filtres optionnels
      if (options.startDate) {
        q = query(q, where('importDate', '>=', Timestamp.fromDate(options.startDate)));
      }
      
      if (options.endDate) {
        q = query(q, where('importDate', '<=', Timestamp.fromDate(options.endDate)));
      }
      
      if (options.supplierId) {
        q = query(q, where('supplierId', '==', options.supplierId));
      }
      
      if (options.importedBy) {
        q = query(q, where('importedBy', '==', options.importedBy));
      }
      
      if (options.status) {
        q = query(q, where('status', '==', options.status));
      }
      
      // Récupérer le nombre total d'enregistrements (sans pagination)
      const totalSnapshot = await getDocs(q);
      const total = totalSnapshot.size;
      
      // Ajouter la pagination
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      if (options.offset) {
        // Pour l'offset, nous devons récupérer le document à partir duquel commencer
        const offsetSnapshot = await getDocs(query(q, limit(options.offset)));
        const lastVisible = offsetSnapshot.docs[offsetSnapshot.docs.length - 1];
        
        if (lastVisible) {
          q = query(q, startAfter(lastVisible));
        }
      }
      
      // Exécuter la requête finale
      const querySnapshot = await getDocs(q);
      
      // Convertir les documents en objets ImportHistory
      const imports = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          importDate: (data.importDate as Timestamp).toDate()
        } as ImportHistory;
      });
      
      return { imports, total };
    } catch (error) {
      console.error('Erreur lors de la récupération de la liste des importations:', error);
      throw new Error('Impossible de récupérer la liste des importations');
    }
  }

  /**
   * Met à jour le statut et le résumé d'une importation
   * @param importId ID de l'importation
   * @param status Nouveau statut
   * @param summary Nouveau résumé
   */
  async updateImportStatus(
    importId: string,
    status: ImportStatus,
    summary: Partial<ImportSummary>
  ): Promise<void> {
    try {
      const importHistoryRef = doc(db, this.collectionName, importId);
      
      await updateDoc(importHistoryRef, {
        status,
        'summary': summary,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut de l\'importation:', error);
      throw new Error('Impossible de mettre à jour le statut de l\'importation');
    }
  }

  /**
   * Supprime une importation et toutes ses données associées
   * @param importId ID de l'importation
   */
  async deleteImport(importId: string): Promise<void> {
    try {
      // Supprimer les transactions importées
      const transactionsRef = collection(db, this.transactionsCollectionName);
      const transactionsQuery = query(transactionsRef, where('importId', '==', importId));
      const transactionsSnapshot = await getDocs(transactionsQuery);
      
      const transactionDeletePromises = transactionsSnapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      
      // Supprimer les véhicules non reconnus
      const unknownVehiclesRef = collection(db, this.unknownVehiclesCollectionName);
      const unknownVehiclesQuery = query(unknownVehiclesRef, where('importId', '==', importId));
      const unknownVehiclesSnapshot = await getDocs(unknownVehiclesQuery);
      
      const unknownVehicleDeletePromises = unknownVehiclesSnapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      
      // Attendre que toutes les suppressions soient terminées
      await Promise.all([...transactionDeletePromises, ...unknownVehicleDeletePromises]);
      
      // Supprimer l'enregistrement d'importation
      const importHistoryRef = doc(db, this.collectionName, importId);
      await deleteDoc(importHistoryRef);
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'importation:', error);
      throw new Error('Impossible de supprimer l\'importation');
    }
  }

  /**
   * Enregistre une transaction importée
   * @param transaction Données de la transaction
   * @returns ID de la transaction créée
   */
  async saveImportedTransaction(
    transaction: Omit<ImportedTransaction, 'id'>
  ): Promise<string> {
    try {
      const transactionsRef = collection(db, this.transactionsCollectionName);
      
      const docRef = await addDoc(transactionsRef, {
        ...transaction,
        createdAt: Timestamp.now()
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de la transaction importée:', error);
      throw new Error('Impossible d\'enregistrer la transaction importée');
    }
  }

  /**
   * Récupère les transactions importées pour une importation
   * @param importId ID de l'importation
   * @param status Statut des transactions à récupérer (optionnel)
   * @returns Liste des transactions
   */
  async getImportedTransactions(
    importId: string,
    status?: 'processed' | 'skipped' | 'error'
  ): Promise<ImportedTransaction[]> {
    try {
      const transactionsRef = collection(db, this.transactionsCollectionName);
      
      let q = query(
        transactionsRef,
        where('importId', '==', importId),
        orderBy('lineNumber', 'asc')
      );
      
      if (status) {
        q = query(q, where('status', '==', status));
      }
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as ImportedTransaction));
    } catch (error) {
      console.error('Erreur lors de la récupération des transactions importées:', error);
      throw new Error('Impossible de récupérer les transactions importées');
    }
  }

  /**
   * Enregistre un véhicule non reconnu
   * @param vehicle Données du véhicule non reconnu
   * @returns ID du véhicule non reconnu créé
   */
  async saveUnknownVehicle(
    vehicle: Omit<UnknownVehicleRecord, 'id'>
  ): Promise<string> {
    try {
      const unknownVehiclesRef = collection(db, this.unknownVehiclesCollectionName);
      
      const docRef = await addDoc(unknownVehiclesRef, {
        ...vehicle,
        createdAt: Timestamp.now()
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement du véhicule non reconnu:', error);
      throw new Error('Impossible d\'enregistrer le véhicule non reconnu');
    }
  }

  /**
   * Récupère les véhicules non reconnus pour une importation
   * @param importId ID de l'importation
   * @param status Statut des véhicules à récupérer (optionnel)
   * @returns Liste des véhicules non reconnus
   */
  async getUnknownVehicles(
    importId: string,
    status?: 'pending' | 'created' | 'ignored'
  ): Promise<UnknownVehicleRecord[]> {
    try {
      const unknownVehiclesRef = collection(db, this.unknownVehiclesCollectionName);
      
      let q = query(
        unknownVehiclesRef,
        where('importId', '==', importId),
        orderBy('transactionCount', 'desc')
      );
      
      if (status) {
        q = query(q, where('status', '==', status));
      }
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as UnknownVehicleRecord));
    } catch (error) {
      console.error('Erreur lors de la récupération des véhicules non reconnus:', error);
      throw new Error('Impossible de récupérer les véhicules non reconnus');
    }
  }

  /**
   * Met à jour le statut d'un véhicule non reconnu
   * @param id ID du véhicule non reconnu
   * @param status Nouveau statut
   * @param createdVehicleId ID du véhicule créé (si applicable)
   */
  async updateUnknownVehicleStatus(
    id: string,
    status: 'pending' | 'created' | 'ignored',
    createdVehicleId?: string
  ): Promise<void> {
    try {
      const unknownVehicleRef = doc(db, this.unknownVehiclesCollectionName, id);
      
      const updateData: Record<string, any> = {
        status,
        updatedAt: Timestamp.now()
      };
      
      if (createdVehicleId) {
        updateData.createdVehicleId = createdVehicleId;
      }
      
      await updateDoc(unknownVehicleRef, updateData);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut du véhicule non reconnu:', error);
      throw new Error('Impossible de mettre à jour le statut du véhicule non reconnu');
    }
  }
}